import React, { useState, useEffect } from 'react';
import Navbar from '../../components/Navbar';
import api from '../../services/api';
import toast from 'react-hot-toast';
import { Upload, AlertTriangle, ArrowLeft, Wallet } from 'lucide-react';
import { Link, useSearchParams } from 'react-router-dom';

const WithdrawPage = () => {
    const [searchParams] = useSearchParams();
    const initialTokenId = searchParams.get('tokenId');

    const [tokens, setTokens] = useState([]);
    const [selectedToken, setSelectedToken] = useState(null);
    const [balances, setBalances] = useState({});
    const [address, setAddress] = useState('');
    const [amount, setAmount] = useState('');
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        fetchData();
    }, []);

    const fetchData = async () => {
        try {
            const [tokensRes, balanceRes] = await Promise.all([
                api.get('/wallet/tokens'),
                api.get('/wallet/balances')
            ]);

            setTokens(tokensRes.data.tokens || []);

            // Map balances for easy lookup
            const balanceMap = {};
            (balanceRes.data.balances || []).forEach(b => {
                if (b.token) balanceMap[b.token.id] = b.balance;
            });
            setBalances(balanceMap);

            if (initialTokenId) {
                const preselected = tokensRes.data.tokens.find(t => String(t.id) === initialTokenId);
                if (preselected) setSelectedToken(preselected);
            }
        } catch (error) {
            toast.error('Failed to load wallet data');
        }
    };

    const handleWithdraw = async (e) => {
        e.preventDefault();
        if (!selectedToken) return;

        setLoading(true);
        try {
            await api.post('/wallet/withdraw', {
                tokenId: selectedToken.id,
                amount: parseFloat(amount),
                address: address
            });
            toast.success('Withdrawal request submitted successfully');
            setAddress('');
            setAmount('');
            // Refresh balances
            fetchData();
        } catch (error) {
            toast.error(error.response?.data?.error || 'Withdrawal failed');
        } finally {
            setLoading(false);
        }
    };

    const getBalance = () => {
        if (!selectedToken) return 0;
        return parseFloat(balances[selectedToken.id] || 0);
    };

    return (
        <div className="bg-[#0b0e11] min-h-screen text-gray-100 font-sans selection:bg-blue-500/30">
            <Navbar />

            <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
                <div className="mb-8">
                    <Link to="/wallet" className="text-gray-400 hover:text-white flex items-center gap-2 mb-4 transition">
                        <ArrowLeft size={20} /> Back to Wallet
                    </Link>
                    <h1 className="text-3xl font-bold text-white flex items-center gap-3">
                        <div className="bg-purple-500/10 p-2 rounded-xl text-purple-500">
                            <Upload size={24} />
                        </div>
                        Withdraw Crypto
                    </h1>
                </div>

                <div className="bg-[#181a20] rounded-3xl border border-gray-800 p-8 shadow-2xl">
                    <form onSubmit={handleWithdraw} className="space-y-8">

                        {/* 1. Select Coin */}
                        <div>
                            <label className="block text-gray-400 mb-2 font-medium bg-[#181a20]">Select Asset</label>
                            <select
                                value={selectedToken?.id || ''}
                                onChange={(e) => {
                                    const token = tokens.find(t => String(t.id) === e.target.value);
                                    setSelectedToken(token);
                                }}
                                className="w-full bg-[#0b0e11] border border-gray-700 rounded-xl px-4 py-4 text-white focus:border-blue-500 outline-none transition text-lg"
                            >
                                <option value="">Select Coin to Withdraw</option>
                                {tokens.map(t => (
                                    <option key={t.id} value={t.id}>
                                        {t.symbol} - {t.name} (Available: {parseFloat(balances[t.id] || 0).toFixed(6)})
                                    </option>
                                ))}
                            </select>
                        </div>

                        {selectedToken && (
                            <>
                                <div className="bg-gray-800/20 p-4 rounded-xl border border-gray-800 flex justify-between items-center">
                                    <span className="text-gray-400">Available Balance</span>
                                    <span className="text-xl font-bold text-white font-mono">{getBalance().toFixed(6)} {selectedToken.symbol}</span>
                                </div>

                                {/* 2. Address */}
                                <div>
                                    <label className="block text-gray-400 mb-2 font-medium">Recipient Address</label>
                                    <input
                                        type="text"
                                        placeholder={`Enter ${selectedToken.symbol} Address`}
                                        value={address}
                                        onChange={(e) => setAddress(e.target.value)}
                                        className="w-full bg-[#0b0e11] border border-gray-700 rounded-xl px-4 py-4 text-white focus:border-blue-500 outline-none transition font-mono"
                                        required
                                    />
                                    <p className="text-xs text-yellow-500/80 mt-2 flex items-center gap-1">
                                        <AlertTriangle size={12} />
                                        Ensure the network matches the withdrawal address.
                                    </p>
                                </div>

                                {/* 3. Amount */}
                                <div>
                                    <div className="flex justify-between mb-2">
                                        <label className="block text-gray-400 font-medium">Amount</label>
                                        <button
                                            type="button"
                                            onClick={() => setAmount(getBalance())}
                                            className="text-blue-500 text-sm font-bold hover:text-blue-400"
                                        >
                                            MAX
                                        </button>
                                    </div>
                                    <div className="relative">
                                        <input
                                            type="number"
                                            step="0.00000001"
                                            placeholder="0.00"
                                            value={amount}
                                            onChange={(e) => setAmount(e.target.value)}
                                            className="w-full bg-[#0b0e11] border border-gray-700 rounded-xl px-4 py-4 text-white focus:border-blue-500 outline-none transition font-mono text-xl"
                                            required
                                            max={getBalance()}
                                        />
                                        <span className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-500 font-bold">{selectedToken.symbol}</span>
                                    </div>
                                </div>

                                {/* Submit */}
                                <button
                                    type="submit"
                                    disabled={loading || parseFloat(amount) <= 0 || !address}
                                    className="w-full bg-blue-600 hover:bg-blue-500 text-white font-bold py-4 rounded-xl transition text-lg shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    {loading ? 'Processing...' : 'Confirm Withdrawal'}
                                </button>
                            </>
                        )}
                    </form>
                </div>
            </div>
        </div>
    );
};

export default WithdrawPage;
